import React from 'react';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4, H5 } from '@/components/Element/Heading';
import MetaTags from '@/components/Head/MetaTags';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import Image from 'next/legacy/image';
import Hero from 'images/solar-customer-resources/PGEIncrease.png';
import ReadingTime from '@/components/ReadingTime/ReadingTime';
import SideCardCTA from '@/components/SideCardCTA/SideCardCTA2';
import FullWidthFormSection from '@/components/LeadForm/FullWidthFormSection';
import Link from 'next/link';
import Head from 'next/head';

const Page = () => {
  return (
    <article>
      <MetaTags
        title="PGE 14% Rate Increase"
        description="Learn more about the recent PGE rate increase in January 2024, it includes a 12.8% increase on homeowners' utility bills."
      />
      <Head>
        <meta
          property="og:image"
          content={
            'https://energypal.com/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FPGEIncrease.eeb275f3.png&w=3840&q=75'
          }
        />
      </Head>
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Resources',
            href: '/solar-customer-resources',
          },
          {
            name: 'PGE 14% Rate Increase',
            href: '/pge-rate-increase',
          },
        ]}
      />
      <CenteredContentSection>
        <Image
          className="drop-shadow-lg rounded-md"
          src={Hero}
          alt="PGE 14% Rate Increase"
        />
        <ReadingTime wordCount={720} />
        <section id="section1">
          <p className="font-bold">
            *Update: This rate increase is applicable between January 2024 until
            the California Flat Rate takes effect, projected for the end of
            2024/beginning of 2025. Read about the new{' '}
            <Link href={'/california-utility-rate-changes'}>
              CA Flat Rate here
            </Link>
            .
          </p>
          <H3>PGE Rate Increase: What It Means for You</H3>
          <p>
            <strong>Have you heard the latest news?</strong> Starting January
            2024, your energy bill is set to jump! If you&apos;re a homeowner in
            California using PG&E, brace yourself for a 12.8% increase in your
            utility bills. Let’s look into why this PGE rate hike is happening
            and what it means for you.
          </p>
          <div className="bg-gray-50 rounded-md p-4 text-black">
            <H4 className="pt-0 mt-0">Key Takeaways</H4>
            <ul>
              <li>
                <strong>Background:</strong> PG&E, California&apos;s primary
                energy provider, is increasing rates.
              </li>
              <li>
                <strong>Impact:</strong> Typical residential customers will see
                a monthly increase of about $32.62.
              </li>
              <li>
                <strong>Reason:</strong> The hike is part of a strategy to
                improve power line safety and reduce wildfire risks.
              </li>
            </ul>
          </div>
          <p>
            The planned rate hike by Pacific Gas & Electric Company (PG&E) for
            January 1st, 2024 has been approved by the California Public
            Utilities Commission. This increase is part of a strategy to fund
            safety projects aimed at reducing the risk of wildfires, which have
            been a significant issue in California. PG&E, serving millions of
            customers across Northern and Central California, will raise rates
            for both electricity and natural gas customers.
          </p>
          <p>
            <strong>
              The rate hike will result in an approximate 12.8% increase in the
              combined monthly bill for natural gas and electricity, translating
              to about $32.62
            </strong>
            .
          </p>

          <p>
            Initially,{' '}
            <strong>
              PG&E had requested a 17.9% increase, amounting to $38.73.
            </strong>{' '}
            The increased rates will finance safety work along electric lines,
            such as burying them underground or covering some lines with more
            fire-resistant materials, especially in areas prone to wildfires. In
            recent years, PG&E has been held responsible for several deadly
            wildfires, attributed to faulty equipment or insufficient
            maintenance of vegetation around electric lines. The company has
            also implemented Power Safety Power Shutoffs, which cut power to
            regions of the state under dry and windy conditions that heighten
            fire risks.
          </p>
        </section>
      </CenteredContentSection>
      <FullWidthFormSection cid="mf2u4ldu665la014" />
      <CenteredContentSection>
        <H4>
          Key Initiatives In The Recent PGE Rate Increase Decision Include:
        </H4>
        <p>
          <strong>Wildfire System Enhancement and Undergrounding:</strong> The
          decision approves the undergrounding of 1,230 miles of electric line
          and the installation of 778 miles of covered conductor, cumulatively
          hardening 2,008 miles of PG&E&apos;s infrastructure. This marks a
          significant move towards safer and more reliable services, optimizing
          costs, and setting the stage for future undergrounding efforts as
          outlined in{' '}
          <a
            href="https://www.cpuc.ca.gov/about-cpuc/divisions/safety-policy-division/risk-assessment-and-safety-analytics/electric-undergrounding-sb-884"
            target="_blank"
            rel="noreferrer"
          >
            Senate Bill 884.
          </a>
        </p>
        <p>
          <strong>Vegetation Management:</strong> PG&E is authorized to invest
          around $1.3 billion in vegetation management to decrease the risk of
          wildfires and enhance system reliability.
        </p>
        <p>
          <strong>Capacity Upgrades:</strong> Over $2.5 billion is allocated for
          PG&E to upgrade its electric distribution system between 2023 and
          2026. This upgrade is geared towards accommodating emerging needs such
          as building electrification, new electric vehicle charging stations,
          and the power demands of new residential and commercial developments.
        </p>
        <H5>
          <strong>What&apos;s the Total Cost?</strong>
        </H5>
        <p>
          {' '}
          What&apos;s the Total Cost? PG&E wanted $15.4 billion for this, but
          CPUC said, &ldquo;Let&apos;s make it $13.5 billion&ldquo;. This means,
          for most people, their bills will go up by about 12.8%, starting
          January 1, 2024.
        </p>
        <H5>
          <strong>What&apos;s Next?</strong>
        </H5>
        <p>
          This is just the start. California&apos;s energy future is about being
          strong against challenges like climate change.
        </p>
        <H4>What Else Can You Do About PGE Rate Increases?</H4>
        <p>
          <strong>Energy Assistance Programs:</strong> You can learn more about
          the income qualified households{' '}
          <a
            target="_blank"
            href={
              'https://www.cpuc.ca.gov/industries-and-topics/electrical-energy/electric-costs/care-fera-program'
            }
            rel="noreferrer"
          >
            programs
          </a>{' '}
          available for those struggling with their energy.{' '}
        </p>
        <p>
          <Link href={'/solar-faq/home-energy-audits-key-things-to-know'}>
            Energy Audit:
          </Link>{' '}
          Another option is to ensure your home is energy efficient. This helps
          you understand how much energy your home consumes and how you can
          improve its efficiency.
        </p>
        <p>
          Learn more about{' '}
          <Link href={'/utilities/pacific-gas-and-electric-company'}>
            PGE during an outage.
          </Link>
        </p>
        <H4>Explore Solar Energy:</H4>
        <p>
          <strong>Quick Tips for Going Solar in PG&E Area</strong> <br /> Here
          are a few quick things to keep in mind if you’re thinking about going
          solar as a PG&E customer:
        </p>
        <p>
          <strong>Understand Net Energy Metering 3.0</strong> – This will be the
          payment structure you’ll adopt when you switch to solar. Read our{' '}
          <Link
            href={
              '/solar-rebates-incentives-and-net-metering-programs/what-is-nem-3-and-how-it-affects-california'
            }
          >
            summary of NEM 3.0
          </Link>
        </p>
        <p>
          <strong>Review available solar incentives</strong> – Our{' '}
          <Link href={'/home-battery-solar-installers-near-me/ca-california'}>
            solar incentives
          </Link>{' '}
          in California article will help you learn about local installers, tax
          credits, and rebates.{' '}
        </p>
        <p>
          You can also connect with us below to learn more about solar for your
          home. Our advisors will walk you through the best options for you and
          your home. based on your current monthly bill.
        </p>
      </CenteredContentSection>
      <FullWidthFormSection cid="mf2u4ldu665la014" />
      <SideCardCTA />
    </article>
  );
};

export default Page;
